import React, { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, Clock, CalendarDays } from "lucide-react";
import Badge from "./ui/Badge";

const statusDot = {
  scheduled: "bg-sky-400",
  ongoing: "bg-emerald-400",
  completed: "bg-white/50",
  expired: "bg-red-400",
};

const statusBorder = {
  scheduled: "border-sky-400/60 bg-sky-400/10",
  ongoing: "border-emerald-400/60 bg-emerald-400/10",
  completed: "border-white/20 bg-white/5",
  expired: "border-red-400/60 bg-red-400/10",
};

export default function Calendar({ interviews = [] }) {
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState(null);

  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1,
  );
  const lastDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0,
  );
  const startingDayOfWeek = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const interviewsByDate = useMemo(() => {
    const grouped = {};
    interviews.forEach((interview) => {
      const date = new Date(interview.scheduledAt);
      const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(interview);
    });
    return grouped;
  }, [interviews]);

  const getInterviewsForDate = (day) => {
    const dateKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${day}`;
    return interviewsByDate[dateKey] || [];
  };

  const getDominantStatus = (day) => {
    const dayInterviews = getInterviewsForDate(day);
    if (dayInterviews.length === 0) return null;
    const statusPriority = { ongoing: 4, scheduled: 3, completed: 2, expired: 1 };
    let dominantStatus = dayInterviews[0].status;
    let highestPriority = statusPriority[dominantStatus] || 0;
    dayInterviews.forEach((interview) => {
      const priority = statusPriority[interview.status] || 0;
      if (priority > highestPriority) {
        highestPriority = priority;
        dominantStatus = interview.status;
      }
    });
    return dominantStatus;
  };

  const previousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
    setSelectedDate(null);
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
    setSelectedDate(null);
  };

  const goToToday = () => {
    const now = new Date();
    setCurrentDate(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDate(null);
  };

  const isToday = (day) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const compareDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day,
    );
    return today.getTime() === compareDate.getTime();
  };

  const handleDateClick = (day) => {
    const dayInterviews = getInterviewsForDate(day);
    if (dayInterviews.length > 0) {
      setSelectedDate(day);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderCalendarDays = () => {
    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <div key={`empty-${i}`} className="aspect-square" aria-hidden="true" />,
      );
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dayInterviews = getInterviewsForDate(day);
      const status = getDominantStatus(day);
      const today = isToday(day);
      const isSelected = selectedDate === day;
      const hasInterviews = dayInterviews.length > 0;
      const clickable = hasInterviews;

      let cellClass =
        "border border-white/5 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.08]";
      if (today) {
        cellClass =
          "border border-amber-400/50 bg-amber-400/15 hover:border-amber-400/70";
      }
      if (status && !today) {
        cellClass = statusBorder[status];
      }
      if (isSelected) {
        cellClass =
          "border-amber-400 bg-amber-400/25 ring-2 ring-amber-400/40 shadow-[0_0_0_1px_rgba(251,191,36,0.3)]";
      }

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => handleDateClick(day)}
          disabled={!clickable}
          aria-label={`${monthNames[currentDate.getMonth()]} ${day}${hasInterviews ? ", has interviews" : ""}`}
          className={`relative flex aspect-square flex-col items-center justify-center rounded-lg text-sm font-medium transition-all duration-150 ${
            clickable ? "cursor-pointer" : "cursor-default"
          } ${
            today || isSelected
              ? "text-cream-50"
              : status
                ? "text-cream-100"
                : "text-amber-200/70"
          } ${cellClass}`}
        >
          <span>{day}</span>
          {hasInterviews && (
            <span className="absolute bottom-1.5 flex gap-1" aria-hidden="true">
              {dayInterviews.slice(0, 3).map((_, idx) => (
                <span
                  key={idx}
                  className={`h-1 w-1 rounded-full ${
                    today || isSelected
                      ? "bg-amber-300"
                      : statusDot[dayInterviews[idx].status] || "bg-amber-400"
                  }`}
                />
              ))}
            </span>
          )}
        </button>
      );
    }

    return days;
  };

  return (
    <div className="card flex h-full flex-col p-5 sm:p-6">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <CalendarDays className="h-5 w-5 text-amber-400" strokeWidth={2} />
          <h2 className="text-lg font-semibold tracking-tight text-cream-100">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={previousMonth}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-amber-200 transition-colors hover:bg-white/10"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={goToToday}
            className="h-8 rounded-lg border border-white/10 bg-white/5 px-3 text-xs font-medium text-amber-200 transition-colors hover:bg-white/10"
          >
            Today
          </button>
          <button
            onClick={nextMonth}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-amber-200 transition-colors hover:bg-white/10"
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mb-2 grid grid-cols-7 gap-1.5">
        {dayNames.map((day) => (
          <div
            key={day}
            className="py-1 text-center text-[11px] font-semibold tracking-wide uppercase text-amber-200/40"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1.5">{renderCalendarDays()}</div>

      <div className="mt-6 border-t border-white/10 pt-5">
        <p className="mb-3 text-xs font-semibold tracking-wide uppercase text-amber-200/50">
          Status legend
        </p>
        <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
          {[
            { label: "Scheduled", cls: "bg-sky-400" },
            { label: "Ongoing", cls: "bg-emerald-400" },
            { label: "Completed", cls: "bg-white/50" },
            { label: "Expired", cls: "bg-red-400" },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2">
              <span
                className={`h-2.5 w-2.5 rounded-full ${item.cls}`}
                aria-hidden="true"
              />
              <span className="text-xs text-amber-100/60">{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {selectedDate && (
        <div className="mt-6 border-t border-white/10 pt-5 animate-fade-in">
          <p className="mb-3 text-xs font-semibold tracking-wide uppercase text-amber-200/50">
            Interviews on {monthNames[currentDate.getMonth()]} {selectedDate}
          </p>
          <div className="flex max-h-48 flex-col gap-2 overflow-y-auto pr-1">
            {getInterviewsForDate(selectedDate).map((interview, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 p-3"
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <Clock className="h-4 w-4 shrink-0 text-amber-300/70" />
                  <span className="truncate font-mono text-xs text-amber-100/80">
                    {formatTime(interview.scheduledAt)} · {interview.roomId}
                  </span>
                </div>
                <Badge status={interview.status} className="shrink-0" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
